import type { Product } from '@scayle/storefront-nuxt'
import { applyOdoscopeCookies } from '@goldner-group/storefront-odoscope'
import type {
  OdoscopeEvent,
  OdoscopeRecommendationRpcResult,
} from '@goldner-group/storefront-odoscope/types'
import { useState } from '#app/composables'
import { useNuxtApp } from '#app/nuxt'
import { useRpc } from '#storefront/composables'
import type { OdoscopeRecommendationsToken } from '#layers/base/constants/recommendations'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { shapeProductCardPayloads } from '#layers/base/utils/productPayload'
import {
  DEFAULT_PRICE_PROMOTION_KEY,
  DEFAULT_PRODUCT_WITH,
} from '#layers/base/constants/withParameters'

export default (
  token: OdoscopeRecommendationsToken,
  categoryId?: number,
  productId?: number,
) => {
  const nuxtApp = useNuxtApp()

  const {
    trackOdoscopeTimeout,
    trackOdoscopeError,
    trackOdoscopeControlGroup,
    trackOdoscopeEmptyResponse,
    trackOdoscopeSuccess,
  } = useTrackingEvents(true)

  // Create a unique key for each recommendation type + context
  const key = `useOdoscopeRecommendations-${token}-${categoryId ?? 'noCat'}-${productId ?? 'noProd'}`
  const recommendations = useState<Product[]>(key, () => [])
  const loading = useState(`${key}-loading`, () => false)
  const fetching = useState(`${key}-fetching`, () => false)
  const error = useState(`${key}-error`, () => false)
  const goControl = useState<boolean>(`${key}-goControl`, () => false)
  const cachedEvent = useState<OdoscopeEvent | undefined>(
    `${key}-odoscopeEvent`,
    () => undefined,
  )
  const hasFetched = useState<boolean>(`${key}-hasFetched`, () => false)

  const getTrackingLabel = (event?: OdoscopeEvent) =>
    event?.label || event?.tokenSlot || token

  const handleOdoscopeEventTracking = (event?: OdoscopeEvent) => {
    if (!event) {
      return
    }

    const label = getTrackingLabel(event)

    if (event.action === 'control') {
      trackOdoscopeControlGroup(label)
      return
    }

    if (event.action === 'empty') {
      trackOdoscopeEmptyResponse(label)
      return
    }

    if (event.action === 'timeout') {
      trackOdoscopeTimeout(label)
      return
    }

    if (event.action === 'error') {
      trackOdoscopeError(label)
      return
    }

    trackOdoscopeSuccess(label)
  }

  const handleErrorResponseTracking = (err: any) => {
    if (err?.response?.status === 408 || err?.status === 408) {
      trackOdoscopeTimeout(token)
    } else {
      trackOdoscopeError(token)
    }
  }

  const retry = async <T>(
    fn: () => Promise<T>,
    retries = 3,
    delay = 1000,
  ): Promise<T> => {
    let lastError: any
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        return await fn()
      } catch (err: any) {
        lastError = err
        if (
          attempt === retries ||
          !(err?.status === 408 || err?.name === 'FetchError')
        ) {
          break
        }
        // setTimeout is the canonical way to delay; promise wrapper required.
        // eslint-disable-next-line promise/avoid-new
        await new Promise<void>((resolve) => setTimeout(resolve, delay))
      }
    }
    throw lastError
  }

  const trackingRefetchInFlight = useState<boolean>(
    `${key}-trackingRefetch`,
    () => false,
  )
  const trackingRefetchUnavailable = useState<boolean>(
    `${key}-trackingRefetchUnavailable`,
    () => false,
  )

  const fetchRecommendationResult = async (
    suffix: 'initial' | 'tracking',
  ): Promise<OdoscopeRecommendationRpcResult | undefined> => {
    const {
      data,
      error: rpcError,
      refresh,
    } = useRpc(
      'getOdoscopeRecommendations',
      `${key}-${suffix}`,
      {
        tokenSlot: token,
        categoryId,
        productId,
        limit: 16,
        with: DEFAULT_PRODUCT_WITH,
        pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
        useGermanSiteId: true,
      },
      { immediate: false, lazy: false, server: false } as any,
    )

    await refresh()

    if (rpcError.value) {
      throw rpcError.value
    }

    applyOdoscopeCookies(data.value?.oscCookie)
    return data.value || undefined
  }

  const refetchForTracking = () => {
    if (trackingRefetchInFlight.value || trackingRefetchUnavailable.value) {
      return
    }
    trackingRefetchInFlight.value = true

    return fetchRecommendationResult('tracking')
      .then((response) => {
        cachedEvent.value = response?.odoscopeEvent
        // Sync the control-group flag from the live tracking refetch. A cached
        // `goControl: false` (from SSR or an earlier non-control fetch) would
        // otherwise keep the `shouldTrack` gate open, so view_item_list/select_item
        // would still fire for control-group users (violates R8). See ECOM-2618.
        goControl.value = Boolean(response?.goControl)
        handleOdoscopeEventTracking(response?.odoscopeEvent)
        return response
      })
      .catch(handleTrackingRefetchError)
      .finally(() => {
        trackingRefetchInFlight.value = false
      })
  }

  const handleTrackingRefetchError = (err: any) => {
    const status = err?.response?.status || err?.statusCode || err?.status || 0
    if (status >= 500 && status !== 408) {
      trackingRefetchUnavailable.value = true
    }
    handleErrorResponseTracking(err)
  }

  const fetchRecommendations = async () => {
    if (nuxtApp.$device?.isCrawler) {
      hasFetched.value = true
      recommendations.value = []
      return recommendations.value
    }

    if (fetching.value) {
      return recommendations.value
    }

    if (recommendations.value.length || hasFetched.value) {
      if (import.meta.client && !nuxtApp.isHydrating) {
        if (cachedEvent.value) {
          handleOdoscopeEventTracking(cachedEvent.value)
        } else {
          refetchForTracking()
        }
      }
      return recommendations.value
    }

    fetching.value = true
    loading.value = true
    error.value = false

    try {
      const response = await retry(() => fetchRecommendationResult('initial'))
      recommendations.value = shapeProductCardPayloads(response?.products ?? [])
      goControl.value = Boolean(response?.goControl)
      cachedEvent.value = response?.odoscopeEvent
      hasFetched.value = true
      error.value =
        response?.odoscopeEvent?.action === 'error' ||
        response?.odoscopeEvent?.action === 'timeout'
      if (!(import.meta.client && nuxtApp.isHydrating)) {
        handleOdoscopeEventTracking(response?.odoscopeEvent)
      }
    } catch (err: any) {
      if (!(import.meta.client && nuxtApp.isHydrating)) {
        handleErrorResponseTracking(err)
      }
      recommendations.value = []
      error.value = true
      goControl.value = false
      hasFetched.value = true
    } finally {
      fetching.value = false
      loading.value = false
    }

    return recommendations.value
  }

  return { recommendations, loading, error, fetchRecommendations, goControl }
}
